import { Link, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  CalendarDays,
  CalendarRange,
  Calendar,
  ClipboardList,
  Clock,
  LayoutDashboard,
  Users,
  Inbox,
  BookOpen,
  RefreshCw,
  LogOut,
  MoreHorizontal,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { clearAdminPassword } from "@/lib/auth/storage";

const primaryLinks = [
  { href: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { href: "/admin/register", label: "Daily", icon: ClipboardList },
  { href: "/admin/notices", label: "Notices", icon: Inbox },
  { href: "/admin/mantras", label: "Mantras", icon: BookOpen },
  { href: "/admin/people", label: "People", icon: Users },
  { href: "/admin/monthly", label: "Monthly", icon: CalendarRange },
];

const moreLinks = [
  { href: "/admin/weekly", label: "Weekly", icon: Calendar },
  { href: "/admin/shifts", label: "Shifts", icon: Clock },
  { href: "/admin/holidays", label: "Holidays", icon: CalendarDays },
  { href: "/admin/sync", label: "Sync devices", icon: RefreshCw },
];

function isActive(pathname: string, href: string, exact?: boolean) {
  return exact ? pathname === href : pathname.startsWith(href);
}

export function AdminNav() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const moreActive = moreLinks.some((l) => isActive(pathname, l.href));

  return (
    <header className="border-b bg-white">
      <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-saffron-700">
            BCEC Attendance
          </p>
          <h1 className="text-lg font-semibold text-foreground">Coordinator</h1>
        </div>
        <nav className="flex flex-wrap items-center gap-1">
          {primaryLinks.map(({ href, label, icon: Icon, exact }) => {
            const active = isActive(pathname, href, exact);
            return (
              <Link
                key={href}
                to={href}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  active
                    ? "bg-saffron-50 text-saffron-800"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{label}</span>
              </Link>
            );
          })}
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  moreActive
                    ? "bg-saffron-50 text-saffron-800"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                <MoreHorizontal className="h-4 w-4" />
                <span className="hidden sm:inline">More</span>
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-52 p-1">
              {moreLinks.map(({ href, label, icon: Icon }) => {
                const active = isActive(pathname, href);
                return (
                  <Link
                    key={href}
                    to={href}
                    className={cn(
                      "flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium",
                      active
                        ? "bg-saffron-50 text-saffron-800"
                        : "text-foreground hover:bg-muted"
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </Link>
                );
              })}
            </PopoverContent>
          </Popover>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              clearAdminPassword();
              navigate("/admin/login");
            }}
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Logout</span>
          </Button>
        </nav>
      </div>
    </header>
  );
}
